import { Button } from "components/DesignSystem/Button/Button";
import { TableUI } from "components/design/TableUI";
import { Header } from "components/DesignSystem/Header/Header";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import TagCreation from "components/tagCreation/tagCreation";
import {
  PERK_STATE_ACTIVE,
  PERK_STATE_PENDING,
  PERK_TAB_ACTIVE,
  PERK_TAB_ALL,
  PERK_TAB_PENDING,
  PERK_TAB_TO_STATE,
  PERKS_PER_PAGE,
  PerkTab,
} from "constants/perksAdmin";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { getCoreRowModel, useReactTable } from "react-table-8.10.7";
import {
  AdminPerk,
  useGetAdminPerksQuery,
  useGetAllPerkTagsQuery,
  useUpdatePerkStateMutation,
} from "store/apis/perksCrm";
import { debounce } from "utils/debouncing";
import { CreatePerkModal } from "./CreatePerkModal";
import { EditPerkModal } from "./EditPerkModal";
import { ManageTagsModal } from "./ManageTagsModal";
import { usePerksTableColumns } from "./usePerksTableColumns";

export default function PerksCrm() {
  usePageTitle("Perks CRM");
  const { isAdmin } = useRoleBasedView();
  const { successToast, alertToast } = useToast();
  const [activeTab, setActiveTab] = useState<PerkTab>(PERK_TAB_ALL);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNum, setPageNum] = useState<number>(1);

  const createPerkModal = useModal();
  const tagCreationModal = useModal();
  const manageTagsModal = useModal();
  const editPerkModal = useModal();
  const [managingTagsPerk, setManagingTagsPerk] = useState<AdminPerk | null>(
    null
  );
  const [editingPerk, setEditingPerk] = useState<AdminPerk | null>(null);

  const handleSearch = useMemo(
    () =>
      debounce((e: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setPageNum(1);
      }, 500),
    []
  );

  const { data, isLoading } = useGetAdminPerksQuery(
    {
      pageNumber: pageNum,
      state: PERK_TAB_TO_STATE[activeTab],
      searchTerm: searchTerm || null,
    },
    { skip: !isAdmin }
  );
  const { data: allTags } = useGetAllPerkTagsQuery();
  const [updatePerkState] = useUpdatePerkStateMutation();

  const {
    perks = [],
    total_pages = 1,
    total_count = 0,
    current_page = 1,
  } = data || {};

  const paginationData = {
    per_page: PERKS_PER_PAGE,
    page_num: current_page,
    total_count,
    total_pages,
  };

  const handleTabChange = (value: string) => {
    if (!value) return;

    setActiveTab(value as PerkTab);
    setPageNum(1);
  };

  const handleActivate = useCallback(
    async (perkId: string) => {
      try {
        await updatePerkState({ perkId, state: PERK_STATE_ACTIVE }).unwrap();
        successToast({ message: "Perk activated" });
      } catch {
        alertToast({ message: "Failed to activate perk" });
      }
    },
    [updatePerkState, successToast, alertToast]
  );

  const handleDeactivate = useCallback(
    async (perkId: string) => {
      try {
        await updatePerkState({ perkId, state: PERK_STATE_PENDING }).unwrap();
        successToast({ message: "Perk deactivated" });
      } catch {
        alertToast({ message: "Failed to deactivate perk" });
      }
    },
    [updatePerkState, successToast, alertToast]
  );

  const handleManageTags = useCallback(
    (perk: AdminPerk) => {
      setManagingTagsPerk(perk);
      manageTagsModal.open();
    },
    [manageTagsModal]
  );

  const handleEditPerk = useCallback(
    (perk: AdminPerk) => {
      setEditingPerk(perk);
      editPerkModal.open();
    },
    [editPerkModal]
  );

  const columns = usePerksTableColumns({
    onManageTags: handleManageTags,
    onEdit: handleEditPerk,
    onActivate: handleActivate,
    onDeactivate: handleDeactivate,
  });

  const table = useReactTable({
    data: perks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
  });

  return (
    <div className="t-w-full">
      <Header
        v2
        title="Perks CRM"
        right={
          <div className="t-flex t-gap-2">
            <Button
              customType="secondary"
              size="small"
              onClick={tagCreationModal.open}
            >
              Create Tag
            </Button>
            <Button
              customType="primary"
              size="small"
              onClick={createPerkModal.open}
            >
              Create Perk
            </Button>
          </div>
        }
      />

      <div className="t-px-5 t-pb-5 t-flex t-flex-col t-gap-4">
        <Search
          onChange={handleSearch}
          placeholder="Search perks..."
          className="t-w-1/2"
        />
        <div className="t-flex t-items-center t-justify-between">
          <ToggleGroup
            value={activeTab}
            onValueChange={handleTabChange}
            size="small"
          >
            <ToggleGroupItem value={PERK_TAB_ALL}>All</ToggleGroupItem>
            <ToggleGroupItem value={PERK_TAB_PENDING}>Pending</ToggleGroupItem>
            <ToggleGroupItem value={PERK_TAB_ACTIVE}>Active</ToggleGroupItem>
          </ToggleGroup>
          <PaginationHandler
            {...paginationData}
            onPageNumChange={(p) => setPageNum(p)}
          />
        </div>

        <TableUI table={table} isLoading={isLoading} />
      </div>

      {createPerkModal.isOpen && (
        <CreatePerkModal onClose={createPerkModal.close} />
      )}

      {tagCreationModal.isOpen && (
        <TagCreation
          constantTag="PERK_TAG"
          show={tagCreationModal.isOpen}
          handleClose={tagCreationModal.close}
        />
      )}

      {manageTagsModal.isOpen && managingTagsPerk && (
        <ManageTagsModal
          perk={managingTagsPerk}
          allTags={allTags}
          onClose={manageTagsModal.close}
        />
      )}

      {editPerkModal.isOpen && editingPerk && (
        <EditPerkModal perk={editingPerk} onClose={editPerkModal.close} />
      )}
    </div>
  );
}
